import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Play, RotateCcw, Trash2, ChevronDown, ChevronUp, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WebhookTestPanel({ token }) {
  const [referredUsers, setReferredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [showLogs, setShowLogs] = useState(false);
  const [commissionLogs, setCommissionLogs] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [deletingLog, setDeletingLog] = useState(null);

  const fetchReferredUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/webhook-test/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setReferredUsers(data);
    } catch (err) {
      toast.error('Failed to load referred users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReferredUsers(); }, [token]);

  const simulatePayment = async (user, isRenewal = false) => {
    setTesting({ userId: user.user_id, type: 'payment' });
    setLastResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/webhook-test/invoice-paid`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          user_id: user.user_id, 
          amount: 9.99,
          is_renewal: isRenewal 
        })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Commission logged: £${data.details.commission_logged.toFixed(2)}`);
        setLastResult(data);
        // Refresh commission logs if showing
        if (showLogs && selectedPartner) {
          fetchCommissionLogs(selectedPartner);
        }
      } else {
        toast.error(data.detail || 'Failed to simulate payment');
      }
    } catch (err) {
      toast.error('Failed to simulate payment');
    } finally {
      setTesting(null);
    }
  };

  const simulateRefund = async (user) => {
    setTesting({ userId: user.user_id, type: 'refund' });
    setLastResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/webhook-test/charge-refunded`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          user_id: user.user_id, 
          amount: 9.99
        })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Commission reversed: -£${data.details.commission_reversed.toFixed(2)}`);
        setLastResult(data);
        // Refresh commission logs if showing
        if (showLogs && selectedPartner) {
          fetchCommissionLogs(selectedPartner);
        }
      } else {
        toast.error(data.detail || 'Failed to simulate refund');
      }
    } catch (err) {
      toast.error('Failed to simulate refund');
    } finally {
      setTesting(null);
    }
  };

  const fetchCommissionLogs = async (partnerId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/webhook-test/commission-logs/${partnerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setCommissionLogs(data.logs || []);
    } catch (err) {
      toast.error('Failed to load commission logs');
    }
  };

  const deleteTestLog = async (logId) => {
    setDeletingLog(logId);
    try {
      const res = await fetch(`${API_URL}/api/admin/webhook-test/commission-logs/${logId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        // Refresh logs
        if (selectedPartner) {
          fetchCommissionLogs(selectedPartner);
        }
      } else {
        toast.error(data.detail || 'Failed to delete log');
      }
    } catch (err) {
      toast.error('Failed to delete log');
    } finally {
      setDeletingLog(null);
    }
  };

  const toggleLogs = async (partnerId) => {
    if (showLogs && selectedPartner === partnerId) {
      setShowLogs(false);
      setSelectedPartner(null);
    } else {
      setSelectedPartner(partnerId);
      setShowLogs(true);
      await fetchCommissionLogs(partnerId);
    }
  };

  // Group users by partner
  const usersByPartner = referredUsers.reduce((acc, user) => {
    const key = user.partner_slug;
    if (!acc[key]) {
      acc[key] = {
        partner_id: user.partner_id,
        partner_name: user.partner_name,
        partner_slug: user.partner_slug,
        commission_rate: user.commission_rate,
        users: []
      };
    }
    acc[key].users.push(user);
    return acc;
  }, {});

  if (loading) {
    return (
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-800 border-slate-700" data-testid="webhook-test-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-5 h-5 text-amber-400" />
          <CardTitle className="text-white text-lg">Webhook Test Mode</CardTitle>
        </div>
        <p className="text-sm text-slate-400">
          Simulate Stripe webhook events to verify commission tracking without real payments
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {Object.keys(usersByPartner).length === 0 ? (
          <div className="text-center py-8">
            <p className="text-slate-400">No users with referral partners found.</p>
            <p className="text-slate-500 text-sm mt-1">
              Create a referral partner, then have a user sign up through their link.
            </p>
          </div>
        ) : (
          <>
            {/* Last Result Banner */}
            {lastResult && (
              <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">{lastResult.message}</p>
                    <p className="text-slate-400 text-sm">
                      {lastResult.details.user_email} → {lastResult.details.partner_name}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${lastResult.details.commission_reversed ? 'text-red-400' : 'text-green-400'}`}>
                      {lastResult.details.commission_reversed ? '-' : '+'}£{(lastResult.details.commission_logged || lastResult.details.commission_reversed).toFixed(2)}
                    </p>
                    <p className="text-slate-500 text-xs">
                      Partner total: £{lastResult.details.new_total_commission.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Users grouped by partner */}
            {Object.entries(usersByPartner).map(([slug, group]) => (
              <div key={slug} className="bg-slate-700/30 rounded-lg p-4 border border-slate-600">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-white font-medium">{group.partner_name}</h3>
                    <p className="text-slate-400 text-xs">
                      Commission rate: {group.commission_rate}% • {group.users.length} referred user(s)
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      // Use partner_id directly from the first user in this group
                      const firstUser = group.users[0];
                      if (firstUser?.partner_id) {
                        toggleLogs(firstUser.partner_id);
                      }
                    }}
                    className="text-slate-400 hover:text-white"
                    data-testid={`view-logs-${slug}`}
                  >
                    {showLogs && selectedPartner ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    <span className="ml-1 text-xs">View Logs</span>
                  </Button>
                </div>

                {/* User list */}
                <div className="space-y-2">
                  {group.users.map(user => (
                    <div key={user.user_id} className="flex items-center justify-between bg-slate-800/50 rounded p-3">
                      <div>
                        <p className="text-white text-sm">{user.email}</p>
                        <p className="text-slate-500 text-xs">{user.full_name}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={user.subscription_active ? 'bg-green-600/20 text-green-400' : 'bg-slate-600 text-slate-400'}>
                          {user.subscription_active ? 'Active' : 'Inactive'}
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-green-600 text-green-400 hover:bg-green-600/20"
                          onClick={() => simulatePayment(user, false)}
                          disabled={testing !== null}
                          data-testid={`simulate-payment-${user.user_id}`}
                        >
                          {testing?.userId === user.user_id && testing.type === 'payment' ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Play className="w-3 h-3" />
                          )}
                          <span className="ml-1 text-xs">Payment</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-600 text-red-400 hover:bg-red-600/20"
                          onClick={() => simulateRefund(user)}
                          disabled={testing !== null}
                          data-testid={`simulate-refund-${user.user_id}`}
                        >
                          {testing?.userId === user.user_id && testing.type === 'refund' ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <RotateCcw className="w-3 h-3" />
                          )}
                          <span className="ml-1 text-xs">Refund</span>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Commission logs panel */}
                {showLogs && selectedPartner && (
                  <div className="mt-4 pt-4 border-t border-slate-600">
                    <h4 className="text-slate-300 text-sm font-medium mb-2">Commission Logs</h4>
                    {commissionLogs.length === 0 ? (
                      <p className="text-slate-500 text-sm">No commission logs yet.</p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {commissionLogs.map(log => (
                          <div key={log.id} className="flex items-center justify-between bg-slate-800 rounded p-2 text-sm">
                            <div className="flex items-center gap-3">
                              <Badge className={log.event_type === 'payment' ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}>
                                {log.event_type}
                              </Badge>
                              <span className="text-slate-400">{log.user_email}</span>
                              <span className="text-slate-500 text-xs">{log.created_at?.slice(0, 10)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${log.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {log.amount >= 0 ? '+' : ''}£{log.amount.toFixed(2)}
                              </span>
                              {log.invoice_id?.startsWith('TEST_') && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-slate-500 hover:text-red-400 h-6 w-6 p-0"
                                  onClick={() => deleteTestLog(log.id)}
                                  disabled={deletingLog === log.id}
                                  title="Delete test log"
                                  data-testid={`delete-log-${log.id}`}
                                >
                                  {deletingLog === log.id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-3 h-3" />
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}
